import React, { useState } from 'react'
import { Label, Popup, Icon, List } from 'semantic-ui-react'

interface VersionLabelProps {
  style?: React.CSSProperties
}

const VersionLabel: React.FC<VersionLabelProps> = ({ style }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  
  // Get version and git info from environment variables
  const appVersion = import.meta.env.VITE_VERSION || 'unknown'
  const gitHash = import.meta.env.VITE_GIT_HASH || 'unknown'
  const gitFullHash = import.meta.env.VITE_GIT_FULL_HASH || ''
  
  // Safely parse git message
  let gitMessage = 'Development Build'
  try {
    if (import.meta.env.VITE_GIT_MESSAGE) {
      gitMessage = JSON.parse(import.meta.env.VITE_GIT_MESSAGE)
    }
  } catch (e) {
    // If JSON parse fails, use the raw value
    gitMessage = import.meta.env.VITE_GIT_MESSAGE || 'Development Build'
  }
  
  const gitDate = import.meta.env.VITE_GIT_DATE || new Date().toISOString()
  const gitBranch = import.meta.env.VITE_GIT_BRANCH || 'unknown'
  const gitRepoUrl = import.meta.env.VITE_GIT_REPO_URL || ''
  const buildTime = import.meta.env.VITE_BUILD_TIME || new Date().toISOString()
  
  // Format dates
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }
  
  // Generate GitHub commit URL
  const githubCommitUrl = gitRepoUrl && gitFullHash && gitFullHash !== 'unknown' 
    ? `${gitRepoUrl}/commit/${gitFullHash}`
    : null
  
  const versionText = `v${appVersion}`
  const shortInfo = `${gitHash} • ${gitBranch}`
  
  return (
    <Popup
      trigger={
        <Label 
          size='mini' 
          basic
          as='a'
          style={{
            ...style,
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'all 0.3s ease',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(34, 36, 38, 0.15)'
          }}
          onMouseEnter={() => setIsExpanded(true)}
          onMouseLeave={() => setIsExpanded(false)}
        >
          <Icon name='code branch' size='small' />
          {versionText}
          {isExpanded && (
            <span style={{ marginLeft: '0.5rem', opacity: 0.7, fontSize: '0.85em' }}>
              • {gitHash} • {gitBranch}
            </span>
          )}
        </Label>
      }
      position='top right'
      wide='very'
      hoverable
      style={{ maxWidth: '400px' }}
    >
      <Popup.Header>
        <Icon name='info circle' />
        Build Information
      </Popup.Header>
      <Popup.Content>
        <List divided relaxed size='small'>
          <List.Item>
            <List.Icon name='tag' />
            <List.Content>
              <List.Header>Version</List.Header>
              <List.Description>{versionText}</List.Description>
            </List.Content>
          </List.Item>
          
          <List.Item>
            <List.Icon name='git' />
            <List.Content>
              <List.Header>Commit</List.Header>
              <List.Description>
                {githubCommitUrl ? (
                  <a 
                    href={githubCommitUrl} 
                    target='_blank' 
                    rel='noopener noreferrer'
                    style={{ color: '#4183c4' }}
                  >
                    {gitHash}
                    <Icon name='external' size='small' style={{ marginLeft: '0.3rem' }} />
                  </a>
                ) : (
                  gitHash
                )}
                {gitMessage && gitMessage !== 'Development Build' && (
                  <div style={{ marginTop: '0.2rem', fontStyle: 'italic', opacity: 0.8 }}>
                    "{gitMessage}"
                  </div>
                )}
              </List.Description>
            </List.Content>
          </List.Item>
          
          <List.Item>
            <List.Icon name='code branch' />
            <List.Content>
              <List.Header>Branch</List.Header>
              <List.Description>{gitBranch}</List.Description>
            </List.Content>
          </List.Item>
          
          <List.Item>
            <List.Icon name='clock' />
            <List.Content>
              <List.Header>Commit Date</List.Header>
              <List.Description>{formatDate(gitDate)}</List.Description>
            </List.Content>
          </List.Item>
          
          <List.Item>
            <List.Icon name='wrench' />
            <List.Content>
              <List.Header>Build Time</List.Header>
              <List.Description>{formatDate(buildTime)}</List.Description>
            </List.Content>
          </List.Item>
          
          {githubCommitUrl && (
            <List.Item>
              <List.Icon name='github' />
              <List.Content>
                <a 
                  href={githubCommitUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{ color: '#4183c4' }}
                >
                  View on GitHub
                  <Icon name='external' size='small' style={{ marginLeft: '0.3rem' }} />
                </a>
              </List.Content>
            </List.Item>
          )}
        </List>
      </Popup.Content>
    </Popup>
  )
}

export default VersionLabel